import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames/bind';

import withComponentName from '../../../decorators/withComponentName';
import withMetatag from '../../../decorators/metadataDecorators/metatagsTitleDecorator';

import * as storyFiltersActions from '../../../actions/storyFilters.action';
import * as watchlistsActions from '../../../actions/watchlistActions';
import * as searchActions from '../../../actions/searchActions';

import { isQueryValid } from '../../../helpers/searchbarHelpers';

import { ADVANCED_SEARCH, searchTypeListWatchlistMapping } from '../../../data/searchbar/types';
import { SILVER } from '../../../data/permissions';

import WatchlistsManageBody from '../../WatchlistComponents/WatchlistManagePageWrapper/WatchlistsManageBody';
import OnboardingWatchlist from './OnboardingWatchlist';
import Preloader from '../../Preloader';
import OnboardingSteps from '../OnboardingSteps';
import OnboardingStepNav from '../OnboardingStepNav';
import NameWatchlistModal from '../../WatchlistComponents/WatchlistManagePageWrapper/NameWatchlistModal';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const OnboardingWatchlistInner = ({
  user, history,
  piwikEnabled,
}) => {
  const dispatch = useDispatch();
  const actions = useMemo(() => (
    bindActionCreators({
      ...storyFiltersActions,
      ...watchlistsActions,
      ...searchActions,
    }, dispatch)
  ), [dispatch]);

  const {
    token,
    watchlistsList,
    activateFilingsPreloader,
    width,
    activeSearchType,
    newWatchlistTopics,
    userKeywords,
    premUser,
    cookiesPolicyStatus,
    searchBarActive,
    promobar,
  } = useSelector((state) => ({
    token: state.watchlistReducer.userToken,
    watchlistsList: state.watchlistReducer.watchlistsList,
    activateFilingsPreloader: state.watchlistReducer.activateFilingsPreloader,
    width: state.common.width,
    activeSearchType: state.searchReducers.activeSearchType,
    newWatchlistTopics: (
      state.searchReducers[
        searchTypeListWatchlistMapping[state.searchReducers.activeSearchType]
      ]
    ),
    userKeywords: state.searchReducers.userKeywords,
    premUser: state.subscriptions.permissions.access_levels.includes(SILVER),
    cookiesPolicyStatus: state.common.cookiesPolicyStatus,
    searchBarActive: state.searchReducers.searchBarActive,
    promobar: state.promobar,
  }));

  const [nameStep, setNameStep] = useState(false);
  const stepNavRef = useRef(null);
  const searchbarInnerRef = useRef(null);

  const resetSearchbarValues = () => {
    actions.removeSearchbarResults();
    actions.addKeyword('');
    actions.setQuery('');
    searchbarInnerRef.current.removeSearchText(true);
  };

  const handleConfirmNameWl = (nameWl) => {
    stepNavRef.current?.handleClickNext(nameWl);
    setNameStep(false);
    resetSearchbarValues();
  };

  const handleCloseNameWl = () => {
    setNameStep(false);
    resetSearchbarValues();
  };

  const handleBackButton = () => {
    setNameStep(false);
    resetSearchbarValues();
  };

  const handleSearchSubmit = () => {
    setNameStep(true);
  };

  const checkButtonDisabled = () => {
    let disabledWLButton = false;

    let isAdvancedQueryValid = false;
    if (activeSearchType === ADVANCED_SEARCH) {
      isAdvancedQueryValid = isQueryValid([
        ...newWatchlistTopics,
        ...(userKeywords?.trim() ? [{
          name: userKeywords?.trim(),
          itemType: 'ENTITY',
        }] : []),
      ]);
      disabledWLButton = !isAdvancedQueryValid;
      return disabledWLButton;
    }
  };

  const disabledWLButton = checkButtonDisabled();

  const renderElements = () => {
    if (activateFilingsPreloader) {
      return (
        <div style={{ flex: '1 1 100%' }}>
          <Preloader centered loading />
          {' '}
        </div>
      );
    }

    if (!watchlistsList?.length) {
      return (
        <OnboardingWatchlist
          searchbarInnerRef={searchbarInnerRef}
          stepNavRef={stepNavRef}
          handleSearchSubmit={handleSearchSubmit}
          resetSearchbarValues={resetSearchbarValues}
          hasBigScreenDesign
        />
      );
    }

    return (
      <WatchlistsManageBody hasBigScreenDesign onboarding />
    );
  };

  useEffect(() => {
    actions.toggleOnboardingProcess(true);
    if (token) {
      actions.getWatchlists();
    } else {
      actions.guestUserWatchlists();
    }
  }, []);

  return (
    <div className={cx('onboarding_watchlist_inner', 'big_screen',
      { promobar: promobar?.isShowed && promobar?.promobarType === 'mobilePromobar' },
      { searchbar: !watchlistsList?.length },
      { searchbar__active: searchBarActive })}
    >
      <OnboardingSteps
        activeStep="Watchlist"
        token={token}
      />
      <div className={cx('onboarding_watchlist_inner__title', { 'prem-user': premUser }, { 'manage-wl': watchlistsList?.length })}>
        <h2>Watchlists</h2>
        {premUser && (
          <div className={cx('onboarding_watchlist_inner__update-message-text', '--premium')}>
            <p>
              Your Premium plan lets you add up to 100 topics and unlimited watchlists!
            </p>
          </div>
        )}
      </div>
      <div className={cx('onboarding_watchlist_inner__content')}>
        {renderElements()}
      </div>
      <div className={cx('onboarding_watchlist_footer', { cookies: cookiesPolicyStatus }, { searchbar_step: !watchlistsList?.length })}>
        <OnboardingStepNav
          activePage="Watchlist"
          manageWatchlists={watchlistsList?.length}
          history={history}
          piwikEnabled={piwikEnabled}
          user={user}
          width={width}
          ref={stepNavRef}
          disabledWLButton={disabledWLButton}
          openNameWlModal={setNameStep}
        />
      </div>
      <NameWatchlistModal
        openModal={nameStep}
        handleConfirmNameWl={handleConfirmNameWl}
        watchlistsList={watchlistsList}
        handleCloseModal={handleCloseNameWl}
        handleBackButton={handleBackButton}
        hasBigScreenDesign
      />
    </div>
  );
};

export default withComponentName(withMetatag(OnboardingWatchlistInner, 'news/onboarding/watchlist'));
